import { useState, useEffect, type Dispatch, type SetStateAction } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, ArrowRight, ArrowUp, ArrowDown,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ArrowLeftRight,
  Plus, Pencil, Edit3, Trash2, Save, Download, Upload, RefreshCw, RotateCcw, Share2, Copy,
  Send, Printer, Search, Filter,
  Check, CheckCircle, X, XCircle, AlertCircle, AlertTriangle, Info, HelpCircle, Eye, EyeOff, Lock, Unlock,
  Play, Pause, Square, StopCircle, SkipForward, SkipBack, Rocket, Star, Bell, BellOff,
  Settings, Settings2, SlidersHorizontal, Link2, Paperclip, Wrench,
  FileText, Package, Truck, Receipt, ClipboardList, Calendar, Building2, Tag, ShoppingCart,
  Mail, Phone, Users, MapPin, BarChart3, TrendingUp, CreditCard, Briefcase, Clock, Archive, Folder, Layers,
  ExternalLink, Home, Monitor, GripVertical, Percent, Store, Calculator,
  type LucideIcon,
} from 'lucide-react'
import {
  getPlate, getDrinkers, getPlates, getPreparations, getCoffeesResolved, getCoffeeResolved, getChildCoffees, getTraits, getIngredients,
  updatePlate, deletePlate,
  addPlateSection, updatePlateSection, removePlateSection,
  addPlateSectionRow, updatePlateSectionRow, removePlateSectionRow,
  addPlateSectionButton, updatePlateSectionButton, removePlateSectionButton,
  addPlateView, updatePlateView, removePlateView,
  addPlateInputParam, updatePlateInputParam, removePlateInputParam,
  verifyPlate, deployPlate, publishToOpenPlace, unpublishFromOpenPlace,
  runDrinker,
} from '../api/brainer'
import { Modal } from '../components/Modal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import { WIDTH_OPTIONS } from './BrainerPlates'
import type {
  BPlate, BPlateSection, BPlateSectionButton, BPlateView, BPlateInputParam,
  BPlateViewCategory, BPlateChartType, BPlateInputParamType, BDrinker,
  BPlateViewColumnDef, BPlateViewRowAction, BPlateViewOutputMapping, BPlateViewPaginationConfig, BPreparation, BIngredient,
  BPlateViewFilterCondition, BPlateViewFilterOp, BPlateViewOrderBy, BPlateViewSpacing, BPlateViewSummaryRow,
} from '../types'

// ── Section button icon registry ──────────────────────────────────────────────

export const BUTTON_ICONS: Record<string, LucideIcon> = {
  ArrowLeft, ArrowRight, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ExternalLink, Home, ArrowLeftRight,
  Plus, Pencil, Edit3, Trash2, Save, Download, Upload, RefreshCw, RotateCcw, Share2, Copy, Send, Printer, Search, Filter, SlidersHorizontal, Link2, Paperclip,
  Check, CheckCircle, X, XCircle, AlertCircle, AlertTriangle, Info, HelpCircle, Eye, EyeOff, Lock, Unlock,
  Play, Pause, Square, StopCircle, SkipForward, SkipBack, Rocket, Star, Bell, BellOff,
  Settings, Settings2, Wrench,
  FileText, Package, Truck, Receipt, ClipboardList, Calendar, Building2, Tag, ShoppingCart, Mail, Phone, Users, MapPin, BarChart3, TrendingUp, CreditCard, Briefcase, Clock, Archive, Folder, Layers, Percent, Store, Calculator,
}

// Same names as BUTTON_ICONS above, organized for browsing in the icon picker (ICON_PICKER_NONE
// is a sentinel — Radix Select.Item forbids an empty-string value, which is otherwise how "no
// icon" would naturally be represented).
const ICON_PICKER_NONE = '__none__'
const ICON_GROUPS: { label: string; names: string[] }[] = [
  { label: 'Navigation', names: ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'ChevronLeft', 'ChevronRight', 'ChevronUp', 'ChevronDown', 'ExternalLink', 'Home', 'ArrowLeftRight'] },
  { label: 'Actions', names: ['Plus', 'Pencil', 'Edit3', 'Trash2', 'Save', 'Download', 'Upload', 'RefreshCw', 'RotateCcw', 'Share2', 'Copy', 'Send', 'Printer', 'Search', 'Filter', 'SlidersHorizontal', 'Link2', 'Paperclip'] },
  { label: 'Status', names: ['Check', 'CheckCircle', 'X', 'XCircle', 'AlertCircle', 'AlertTriangle', 'Info', 'HelpCircle', 'Eye', 'EyeOff', 'Lock', 'Unlock'] },
  { label: 'Media & controls', names: ['Play', 'Pause', 'Square', 'StopCircle', 'SkipForward', 'SkipBack', 'Rocket', 'Star', 'Bell', 'BellOff'] },
  { label: 'Settings', names: ['Settings', 'Settings2', 'Wrench'] },
  { label: 'Business', names: ['FileText', 'Package', 'Truck', 'Receipt', 'ClipboardList', 'Calendar', 'Building2', 'Tag', 'ShoppingCart', 'Mail', 'Phone', 'Users', 'MapPin', 'BarChart3', 'TrendingUp', 'CreditCard', 'Briefcase', 'Clock', 'Archive', 'Folder', 'Layers', 'Percent', 'Store', 'Calculator'] },
]

function IconPicker({ value, onChange }: { value: string; onChange: (name: string) => void }) {
  return (
    <Select value={value || ICON_PICKER_NONE} onValueChange={v => onChange(v === ICON_PICKER_NONE ? '' : v)}>
      <SelectItem value={ICON_PICKER_NONE}>— none —</SelectItem>
      {ICON_GROUPS.map(({ label, names }) => (
        <div key={label}>
          <div className="px-2.5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-dim">{label}</div>
          {names.map(name => {
            const Icon = BUTTON_ICONS[name]
            return (
              <SelectItem key={name} value={name}>
                <span className="flex items-center gap-2"><Icon size={14} className="shrink-0" /> {name}</span>
              </SelectItem>
            )
          })}
        </div>
      ))}
    </Select>
  )
}

function ButtonPreview({ label, variant, icon }: { label: string; variant?: string; icon?: string }) {
  const IconComp = icon ? BUTTON_ICONS[icon] : undefined
  const cls = variant ? `btn-${variant}` : 'btn-secondary'
  return (
    <button type="button" className={`${cls} pointer-events-none`}>
      {IconComp && <IconComp size={14} />}
      {label}
    </button>
  )
}

// ── Plate detail ───────────────────────────────────────────────────────────────

const CATEGORIES: BPlateViewCategory[] = ['list', 'table', 'chart', 'card', 'text', 'title', 'button', 'preparation']
const CHART_TYPES: BPlateChartType[]   = ['bar', 'line', 'pie', 'area']
const PARAM_TYPES: BPlateInputParamType[] = ['string', 'number', 'date', 'uuid']

const STATUS_BADGE: Record<string, string> = {
  draft:    'bg-slate-100 text-slate-500',
  verified: 'bg-blue-50 text-blue-600',
  deployed: 'bg-emerald-50 text-emerald-600',
  error:    'bg-red-50 text-red-500',
}

type ViewFormValues = {
  title: string
  description: string
  category: BPlateViewCategory
  maxHeight: number
  rank: number
  drinkerId: string
  resultKey: string
  chartType: BPlateChartType
  inputParamId: string
  coffeeApiMode: 'cup_list' | 'single' | 'children'
  childCupTitle: string
  dataPath: string
  columnConfig: BPlateViewColumnDef[]
  rowActions: BPlateViewRowAction[]
  outputMappings: BPlateViewOutputMapping[]
  paginationConfig?: BPlateViewPaginationConfig
  filterConditions: BPlateViewFilterCondition[]
  orderBy: BPlateViewOrderBy[]
  backgroundColor: string
  borderColor: string
  borderStyle: string
  borderWidth: string
  borderRadius: string
  preparationId: string
  // 'preparation' views: show the form right on the page instead of a button + modal.
  inline: boolean
  // 'table' views: invoice-style totals under the rows.
  summaryRows: BPlateViewSummaryRow[]
  // Unset (undefined) on save means "never configured" — the view renders exactly as it always
  // has. showTitle/showDescription always carry an explicit true/false (Settings, step 1, isn't
  // skippable); spacing stays undefined until the admin actually edits a padding/margin field in
  // the optional Styling step, so skipping that step leaves an existing view's spacing untouched.
  showTitle: boolean
  showDescription: boolean
  spacing?: BPlateViewSpacing
}

const DEFAULT_VIEW: ViewFormValues = {
  title: '', description: '',
  category: 'list',
  maxHeight: 400, rank: 0,
  drinkerId: '', resultKey: '',
  chartType: 'bar',
  inputParamId: '',
  coffeeApiMode: 'cup_list',
  childCupTitle: '',
  dataPath: '',
  columnConfig: [],
  rowActions: [],
  outputMappings: [],
  paginationConfig: undefined,
  filterConditions: [],
  orderBy: [],
  backgroundColor: '',
  borderColor: '',
  borderStyle: '',
  borderWidth: '',
  borderRadius: '',
  preparationId: '',
  inline: false,
  summaryRows: [],
  showTitle: false,
  showDescription: false,
  spacing: undefined,
}

// ── BrainerPlateDetail ────────────────────────────────────────────────────────

export function BrainerPlateDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: plate, isLoading } = useQuery({
    queryKey: ['b-plate', id],
    queryFn: () => getPlate(id!).then(r => r.data),
    enabled: !!id,
  })
  const { data: drinkers = [] } = useQuery({
    queryKey: ['b-drinkers'],
    queryFn: () => getDrinkers().then(r => r.data),
  })
  const { data: plates = [] } = useQuery({
    queryKey: ['b-plates'],
    queryFn: () => getPlates().then(r => r.data),
  })
  const { data: preparations = [] } = useQuery({
    queryKey: ['b-preps'],
    queryFn: () => getPreparations().then(r => r.data),
  })

  const inv = () => qc.invalidateQueries({ queryKey: ['b-plate', id] })

  // ── Tab state ─────────────────────────────────────────────────────────────
  const [tab, setTab] = useState<'sections' | 'inputs'>('sections')

  // ── Plate edit state ──────────────────────────────────────────────────────
  const [showEditPlate, setShowEditPlate] = useState(false)
  const [showDeletePlate, setShowDeletePlate] = useState(false)
  const [plateForm, setPlateForm] = useState({ name: '', description: '', subjectType: '', width: '', headerTitle: '' })
  const savePlate = useMutation({
    mutationFn: () => updatePlate(id!, {
      name: plateForm.name,
      description: plateForm.description || undefined,
      subjectType: plateForm.subjectType || undefined,
      width: plateForm.width || undefined,
      headerTitle: plateForm.headerTitle || undefined,
    }),
    onSuccess: () => { inv(); setShowEditPlate(false) },
  })
  const openEditPlate = () => {
    if (!plate) return
    setPlateForm({
      name: plate.name,
      description: plate.description ?? '',
      subjectType: plate.subjectType ?? '',
      width: plate.width ?? '',
      headerTitle: plate.headerTitle ?? '',
    })
    setShowEditPlate(true)
  }
  const deletePlateMutation = useMutation({
    mutationFn: () => deletePlate(id!),
    onSuccess: () => navigate('/brainer/plates'),
  })

  // ── Section state ─────────────────────────────────────────────────────────
  const [showAddSection, setShowAddSection]   = useState(false)
  const [editSection, setEditSection]         = useState<BPlateSection | null>(null)
  const [deleteSection, setDeleteSection]     = useState<BPlateSection | null>(null)
  const [sectionForm, setSectionForm]         = useState({ title: '', rank: 0, width: 50 })
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())

  // ── Row state ─────────────────────────────────────────────────────────────
  const [deleteRow, setDeleteRow] = useState<{ id: string; viewCount: number } | null>(null)
  const addRow = useMutation({
    mutationFn: ({ sectionId, rank, isTabbed }: { sectionId: string; rank: number; isTabbed?: boolean }) => addPlateSectionRow(sectionId, rank, isTabbed),
    onSuccess: () => inv(),
  })
  const saveRow = useMutation({
    mutationFn: ({ rowId, rank, isTabbed }: { rowId: string; rank?: number; isTabbed?: boolean }) => updatePlateSectionRow(rowId, { rank, isTabbed }),
    onSuccess: () => inv(),
  })
  const delRow = useMutation({
    mutationFn: (rowId: string) => removePlateSectionRow(rowId),
    onSuccess: () => { inv(); setDeleteRow(null) },
  })

  // ── View state ────────────────────────────────────────────────────────────
  const [showAddView, setShowAddView] = useState<{ sectionId: string; rowId: string } | null>(null)
  const [editView, setEditView]         = useState<BPlateView | null>(null)
  const [deleteView, setDeleteView]     = useState<BPlateView | null>(null)
  const [wizardInitial, setWizardInitial] = useState<ViewFormValues>(DEFAULT_VIEW)

  // ── Param state ───────────────────────────────────────────────────────────
  const [showAddParam, setShowAddParam] = useState(false)
  const [editParam, setEditParam]       = useState<BPlateInputParam | null>(null)
  const [deleteParam, setDeleteParam]   = useState<BPlateInputParam | null>(null)
  const [paramForm, setParamForm]       = useState({ name: '', description: '', paramType: 'string' as BPlateInputParamType, required: false, fixedValue: '' })

  // ── Section mutations ─────────────────────────────────────────────────────
  const addSection = useMutation({
    mutationFn: () => addPlateSection(id!, { title: sectionForm.title || undefined, rank: sectionForm.rank, width: sectionForm.width }),
    onSuccess: () => { inv(); setShowAddSection(false) },
  })
  const saveSection = useMutation({
    mutationFn: () => updatePlateSection(editSection!.id, { title: sectionForm.title || undefined, rank: sectionForm.rank, width: sectionForm.width }),
    onSuccess: () => { inv(); setEditSection(null) },
  })
  const delSection = useMutation({
    mutationFn: (sid: string) => removePlateSection(sid),
    onSuccess: () => { inv(); setDeleteSection(null) },
  })

  // ── View mutations ────────────────────────────────────────────────────────
  const addViewMutation = useMutation({
    mutationFn: ({ sectionId, rowId, data }: { sectionId: string; rowId: string; data: ViewFormValues }) =>
      addPlateView(sectionId, {
        ...data,
        plateSectionRowId: rowId,
        drinkerId:        data.drinkerId      || undefined,
        resultKey:        data.resultKey      || undefined,
        inputParamId:     data.inputParamId   || undefined,
        childCupTitle:    data.childCupTitle  || undefined,
        dataPath:         data.dataPath       || undefined,
        columnConfig:     data.columnConfig.length        ? data.columnConfig        : undefined,
        rowActions:       data.rowActions.length          ? data.rowActions          : undefined,
        outputMappings:   data.outputMappings.length      ? data.outputMappings      : undefined,
        paginationConfig: data.paginationConfig?.pageParamName ? data.paginationConfig : undefined,
        filterConditions: data.filterConditions.length    ? data.filterConditions    : undefined,
        orderBy:          data.orderBy.length             ? data.orderBy             : undefined,
        backgroundColor:  data.backgroundColor || undefined,
        borderColor:      data.borderColor     || undefined,
        borderStyle:      data.borderStyle     || undefined,
        borderWidth:      data.borderWidth     || undefined,
        borderRadius:     data.borderRadius    || undefined,
        preparationId:    data.preparationId   || undefined,
        showTitle:        data.showTitle,
        showDescription:  data.showDescription,
        spacing:          data.spacing,
      }),
    onSuccess: () => { inv(); setShowAddView(null) },
  })
  const saveViewMutation = useMutation({
    mutationFn: (data: ViewFormValues) =>
      updatePlateView(editView!.id, {
        ...data,
        drinkerId:        data.drinkerId      || undefined,
        resultKey:        data.resultKey      || undefined,
        inputParamId:     data.inputParamId   || undefined,
        childCupTitle:    data.childCupTitle  || undefined,
        dataPath:         data.dataPath       || undefined,
        columnConfig:     data.columnConfig.length        ? data.columnConfig        : undefined,
        rowActions:       data.rowActions.length          ? data.rowActions          : undefined,
        outputMappings:   data.outputMappings.length      ? data.outputMappings      : undefined,
        paginationConfig: data.paginationConfig?.pageParamName ? data.paginationConfig : undefined,
        filterConditions: data.filterConditions.length    ? data.filterConditions    : undefined,
        orderBy:          data.orderBy.length             ? data.orderBy             : undefined,
        backgroundColor:  data.backgroundColor || undefined,
        borderColor:      data.borderColor     || undefined,
        borderStyle:      data.borderStyle     || undefined,
        borderWidth:      data.borderWidth     || undefined,
        borderRadius:     data.borderRadius    || undefined,
        preparationId:    data.preparationId   || undefined,
        showTitle:        data.showTitle,
        showDescription:  data.showDescription,
        spacing:          data.spacing,
      }),
    onSuccess: () => { inv(); setEditView(null) },
  })
  const delView = useMutation({
    mutationFn: (vid: string) => removePlateView(vid),
    onSuccess: () => { inv(); setDeleteView(null) },
  })

  // ── Param mutations ───────────────────────────────────────────────────────
  const addParam = useMutation({
    mutationFn: () => addPlateInputParam(id!, { ...paramForm, description: paramForm.description || undefined, fixedValue: paramForm.fixedValue || undefined }),
    onSuccess: () => { inv(); setShowAddParam(false) },
  })
  const saveParam = useMutation({
    mutationFn: () => updatePlateInputParam(editParam!.id, { ...paramForm, description: paramForm.description || undefined, fixedValue: paramForm.fixedValue || undefined }),
    onSuccess: () => { inv(); setEditParam(null) },
  })
  const delParam = useMutation({
    mutationFn: (pid: string) => removePlateInputParam(pid),
    onSuccess: () => { inv(); setDeleteParam(null) },
  })

  // ── Button state ──────────────────────────────────────────────────────────
  type BtnForm = { label: string; destinationPlateId: string; preparationId: string; variant: string; icon: string }
  const BLANK_BTN: BtnForm = { label: '', destinationPlateId: '', preparationId: '', variant: 'secondary', icon: '' }
  const [addButtonSection, setAddButtonSection] = useState<string | null>(null)
  const [editButton, setEditButton]             = useState<BPlateSectionButton | null>(null)
  const [buttonForm, setButtonForm]             = useState<BtnForm>(BLANK_BTN)

  // ── Button mutations ──────────────────────────────────────────────────────
  const addBtn = useMutation({
    mutationFn: () => addPlateSectionButton(addButtonSection!, {
      label: buttonForm.label,
      destinationPlateId: buttonForm.destinationPlateId || undefined,
      preparationId: buttonForm.preparationId || undefined,
      variant: buttonForm.variant || undefined,
      icon: buttonForm.icon || undefined,
    }),
    onSuccess: () => { inv(); setAddButtonSection(null); setButtonForm(BLANK_BTN) },
  })
  const saveBtn = useMutation({
    mutationFn: () => updatePlateSectionButton(editButton!.id, {
      label: buttonForm.label,
      destinationPlateId: buttonForm.destinationPlateId || undefined,
      preparationId: buttonForm.preparationId || undefined,
      variant: buttonForm.variant || undefined,
      icon: buttonForm.icon || undefined,
    }),
    onSuccess: () => { inv(); setEditButton(null); setButtonForm(BLANK_BTN) },
  })
  const delBtn = useMutation({
    mutationFn: (bid: string) => removePlateSectionButton(bid),
    onSuccess: () => inv(),
  })

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  const verify  = useMutation({ mutationFn: () => verifyPlate(id!), onSuccess: () => inv() })
  const deploy  = useMutation({ mutationFn: () => deployPlate(id!), onSuccess: () => inv() })
  const publish = useMutation({
    mutationFn: () => plate?.openPlacePublished ? unpublishFromOpenPlace(id!) : publishToOpenPlace(id!),
    onSuccess: () => inv(),
  })

  const openAddSection = () => {
    setSectionForm({ title: '', rank: plate?.sections.length ?? 0, width: 50 })
    setShowAddSection(true)
  }
  const openEditSection = (s: BPlateSection) => {
    setSectionForm({ title: s.title ?? '', rank: s.rank, width: s.width })
    setEditSection(s)
  }
  const openAddView = (sectionId: string, rowId: string, count: number) => {
    setWizardInitial({ ...DEFAULT_VIEW, rank: count })
    setShowAddView({ sectionId, rowId })
  }
  const openEditView = (v: BPlateView) => {
    setWizardInitial({
      title: v.title, description: v.description ?? '',
      category: v.category, maxHeight: v.maxHeight, rank: v.rank,
      drinkerId: v.drinkerId ?? '', resultKey: v.resultKey ?? '',
      chartType: v.chartType,
      inputParamId: v.inputParamId ?? '',
      coffeeApiMode: v.coffeeApiMode ?? 'cup_list',
      childCupTitle: v.childCupTitle ?? '',
      dataPath: v.dataPath ?? '',
      columnConfig: v.columnConfig ?? [],
      rowActions: v.rowActions ?? [],
      outputMappings: v.outputMappings ?? [],
      paginationConfig: v.paginationConfig ?? undefined,
      filterConditions: v.filterConditions ?? [],
      orderBy: v.orderBy ?? [],
      backgroundColor: v.backgroundColor ?? '',
      borderColor: v.borderColor ?? '',
      borderStyle: v.borderStyle ?? '',
      borderWidth: v.borderWidth ?? '',
      borderRadius: v.borderRadius ?? '',
      preparationId: v.preparationId ?? '',
      inline: v.inline ?? false,
      summaryRows: v.summaryRows ?? [],
      // Reflect what this view is actually showing today: an existing, never-configured view has
      // showTitle/showDescription === undefined but is currently visible, so default to checked —
      // only DEFAULT_VIEW (brand-new views) defaults to unchecked.
      showTitle: v.showTitle ?? true,
      showDescription: v.showDescription ?? true,
      spacing: v.spacing,
    })
    setEditView(v)
  }
  const openAddParam = () => {
    setParamForm({ name: '', description: '', paramType: 'string', required: false, fixedValue: '' })
    setShowAddParam(true)
  }
  const openEditParam = (p: BPlateInputParam) => {
    setParamForm({ name: p.name, description: p.description ?? '', paramType: p.paramType, required: p.required, fixedValue: p.fixedValue ?? '' })
    setEditParam(p)
  }
  const toggleSection = (sid: string) =>
    setCollapsedSections(prev => { const next = new Set(prev); next.has(sid) ? next.delete(sid) : next.add(sid); return next })

  if (isLoading) return <div className="p-8 text-center text-sm text-neutral-400">Loading</div>
  if (!plate)   return <div className="p-8 text-center text-sm text-neutral-400">Plate not found</div>

  const badge = STATUS_BADGE[plate.status] ?? STATUS_BADGE.draft
  const totalWidth = plate.sections.reduce((sum, s) => sum + s.width, 0)

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/brainer/plates')} className="mt-0.5 text-slate-400 hover:text-slate-700 transition-colors">
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-neutral-900">{plate.name}</h1>
              <span className={`px-2 py-0.5 text-xs rounded-full ${badge}`}>{plate.status}</span>
              <button onClick={openEditPlate} className="text-slate-400 hover:text-slate-700 transition-colors"><Pencil size={13} /></button>
              <button onClick={() => setShowDeletePlate(true)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
            </div>
            {plate.description && <p className="text-sm text-neutral-400 mt-0.5">{plate.description}</p>}
            {plate.subjectType && (
              <p className="text-xs text-slate-400 mt-0.5">Subject: <span className="font-mono text-slate-600">{plate.subjectType}</span></p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={openAddSection} icon={Plus}>
            Add section
          </Button>
          <Button variant="secondary" size="sm" onClick={openAddParam} icon={Plus}>
            Add input
          </Button>
          {(plate.status === 'draft' || plate.status === 'error') && (
            <Button variant="secondary" onClick={() => verify.mutate()} disabled={verify.isPending} icon={CheckCircle}>
              {verify.isPending ? 'Verifying…' : 'Verify'}
            </Button>
          )}
          {plate.status === 'verified' && (
            <Button onClick={() => deploy.mutate()} disabled={deploy.isPending} icon={Rocket}>
              {deploy.isPending ? 'Deploying…' : 'Deploy'}
            </Button>
          )}
          {plate.status === 'deployed' && (
            <>
              <Button
                variant="secondary"
                onClick={() => publish.mutate()}
                disabled={publish.isPending}
                className={plate.openPlacePublished ? 'text-emerald-600' : ''}
                title={plate.openPlacePublished ? 'Unpublish from OpenPlace' : 'Publish to OpenPlace'}
                icon={Monitor}
              >
                {publish.isPending ? '…' : plate.openPlacePublished ? 'Published' : 'Publish'}
              </Button>
              <Button onClick={() => navigate(`/brainer/plates/${id}/run`)} icon={Play}>
                Run
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── Error message ───────────────────────────────────────── */}
      {plate.status === 'error' && plate.errorMessage && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 p-3">
          <div className="flex items-center gap-1.5 text-red-600 text-xs font-medium mb-1">
            <AlertCircle size={12} /> Verification errors
          </div>
          {plate.errorMessage.split('\n').map((line, i) => (
            <p key={i} className="text-xs text-red-500 ml-4">• {line}</p>
          ))}
        </div>
      )}

      {/* ── Tabs ────────────────────────────────────────────────── */}
      <div className="border-b border-slate-100 mb-5">
        <nav className="flex -mb-px">
          {(['sections', 'inputs'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize ${
                tab === t
                  ? 'border-brand-500 text-brand-500'
                  : 'border-transparent text-gray-500 hover:text-neutral-700'
              }`}
            >
              {t === 'sections' ? 'Sections' : 'Inputs'}
              {t === 'sections' && plate.sections.length > 0 && (
                <span className="ml-1.5 text-[10px] bg-slate-100 text-neutral-400 px-1.5 py-0.5 rounded-full">
                  {plate.sections.length}
                </span>
              )}
              {t === 'inputs' && plate.inputParams.length > 0 && (
                <span className="ml-1.5 text-[10px] bg-slate-100 text-neutral-400 px-1.5 py-0.5 rounded-full">
                  {plate.inputParams.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Sections tab ────────────────────────────────────────── */}
      {tab === 'sections' && (
        <div>
          {totalWidth > 0 && (
            <div className="flex items-center gap-2 mb-3 text-xs text-slate-400">
              <span>{totalWidth}% used</span>
              {totalWidth > 100 && <><AlertCircle size={12} className="text-red-500" /><span className="text-red-500">exceeds 100%</span></>}
            </div>
          )}
          {plate.sections.length === 0 ? (
            <div className="rounded border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
              Add a section to start building your plate layout
            </div>
          ) : (
            <div className="flex gap-3 items-start flex-wrap">
              {[...plate.sections].sort((a, b) => a.rank - b.rank).map((section) => {
                const collapsed = collapsedSections.has(section.id)
                return (
                  <div key={section.id} style={{ width: `calc(${section.width}% - 6px)`, minWidth: '160px' }} className="flex-shrink-0 border border-slate-200 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center gap-2">
                        <GripVertical size={13} className="text-slate-300" />
                        <span className="text-sm font-medium text-slate-700">
                          {section.title || `Section ${section.rank + 1}`}
                        </span>
                        <span className="text-theme-xs text-slate-400 font-mono">rank {section.rank} · {section.width}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditSection(section)} className="text-slate-400 hover:text-slate-700 transition-colors"><Pencil size={12} /></button>
                        <button onClick={() => setDeleteSection(section)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={12} /></button>
                        <button onClick={() => toggleSection(section.id)} className="text-slate-400 hover:text-slate-600 transition-colors">
                          {collapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
                        </button>
                      </div>
                    </div>
                    {!collapsed && (
                      <div className="p-3 space-y-2">
                        {[...(section.rows ?? [])].sort((a, b) => a.rank - b.rank).map((row) => {
                          const rowViews = (section.views ?? []).filter(v => v.plateSectionRowId === row.id).sort((a, b) => a.rank - b.rank)
                          return (
                            <div key={row.id} className={`border rounded p-1.5 space-y-1.5 ${row.isTabbed ? 'border-accent/30 bg-accent-soft/30' : 'border-slate-100 bg-slate-50/50'}`}>
                              <div className="flex items-center justify-between px-0.5">
                                <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono">
                                  <span className="flex items-center gap-1">
                                    row
                                    <input
                                      type="number"
                                      defaultValue={row.rank}
                                      onBlur={e => {
                                        const rank = Number(e.target.value)
                                        if (rank !== row.rank) saveRow.mutate({ rowId: row.id, rank })
                                      }}
                                      className="w-10 bg-transparent border-b border-slate-200 focus:outline-none focus:border-accent"
                                    />
                                  </span>
                                  <label className="flex items-center gap-1 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={row.isTabbed}
                                      onChange={e => saveRow.mutate({ rowId: row.id, isTabbed: e.target.checked })}
                                      className="rounded"
                                    />
                                    Tab row
                                  </label>
                                  {row.isTabbed && <span className="text-accent">— view rank sets tab order, rank 0 is active by default</span>}
                                </div>
                                <button onClick={() => setDeleteRow({ id: row.id, viewCount: rowViews.length })} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={10} /></button>
                              </div>
                              <div className="flex gap-1.5 items-start">
                                {rowViews.map((view) => (
                                  <div key={view.id} className="flex-1 min-w-0">
                                    <ViewCard
                                      view={view}
                                      drinkers={drinkers as BDrinker[]}
                                      inputParams={plate.inputParams}
                                      onEdit={() => openEditView(view)}
                                      onDelete={() => setDeleteView(view)}
                                    />
                                  </div>
                                ))}
                              </div>
                              <button
                                onClick={() => openAddView(section.id, row.id, rowViews.length)}
                                className="w-full border border-dashed border-slate-200 rounded p-1.5 text-theme-xs text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors flex items-center justify-center gap-1"
                              >
                                <Plus size={10} /> {row.isTabbed ? 'Add tab to row' : 'Add view to row'}
                              </button>
                            </div>
                          )
                        })}
                        <button
                          onClick={() => addRow.mutate({ sectionId: section.id, rank: (section.rows ?? []).length })}
                          disabled={addRow.isPending}
                          className="w-full border border-dashed border-slate-200 rounded p-2 text-xs text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors flex items-center justify-center gap-1"
                        >
                          <Plus size={11} /> Add row
                        </button>

                        {/* Section buttons */}
                        <div className="pt-1 border-t border-slate-100">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-theme-xs font-medium text-slate-500">Buttons</span>
                            <button
                              onClick={() => { setAddButtonSection(section.id); setButtonForm(BLANK_BTN) }}
                              className="text-theme-xs text-slate-400 hover:text-slate-600 flex items-center gap-0.5"
                            >
                              <Plus size={10} /> Add
                            </button>
                          </div>
                          {[...(section.buttons ?? [])].sort((a, b) => a.position - b.position).map(btn => (
                            <div key={btn.id} className="flex items-center justify-between px-2 py-1 rounded bg-slate-50 mb-1">
                              <span className="text-theme-xs text-slate-700">{btn.label}</span>
                              <div className="flex gap-1.5">
                                <button onClick={() => { setEditButton(btn); setButtonForm({ label: btn.label, destinationPlateId: btn.destinationPlateId ?? '', preparationId: btn.preparationId ?? '', variant: btn.variant ?? 'secondary', icon: btn.icon ?? '' }) }} className="text-slate-400 hover:text-slate-600"><Pencil size={10} /></button>
                                <button onClick={() => delBtn.mutate(btn.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={10} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Inputs tab ──────────────────────────────────────────── */}
      {tab === 'inputs' && (
        <div className="max-w-lg">
          {plate.inputParams.length === 0 ? (
            <div className="rounded border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">
              No parameters — this plate runs without user input
            </div>
          ) : (
            <div className="space-y-1.5">
              {plate.inputParams.map((p) => (
                <div key={p.id} className="border border-slate-100 rounded p-2.5 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">{p.name}</span>
                    <div className="flex gap-1.5">
                      <button onClick={() => openEditParam(p)} className="text-slate-400 hover:text-slate-600 transition-colors"><Pencil size={11} /></button>
                      <button onClick={() => setDeleteParam(p)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={11} /></button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-theme-xs font-mono text-slate-400">{p.paramType}</span>
                    {p.required && <span className="text-[10px] bg-amber-50 text-amber-600 px-1 rounded">required</span>}
                    {p.fixedValue && <span className="text-[10px] bg-sky-50 text-sky-600 px-1 rounded font-mono">fixed: {p.fixedValue}</span>}
                  </div>
                  {p.description && <p className="text-theme-xs text-slate-400 mt-0.5">{p.description}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Button modals ──────────────────────────────────────── */}
      {(addButtonSection || editButton) && (
        <Modal title={editButton ? 'Edit button' : 'Add button'} onClose={() => { setAddButtonSection(null); setEditButton(null); setButtonForm(BLANK_BTN) }} size="sm">
          <div className="space-y-3">
            <Field label="Label *">
              <input value={buttonForm.label} onChange={e => setButtonForm(f => ({ ...f, label: e.target.value }))} className="field" placeholder="e.g. Back to list" autoFocus />
            </Field>

            <Field label="Variant">
              <div className="grid grid-cols-3 gap-2">
                {(['primary','secondary','danger','warning','success','ghost'] as const).map(v => (
                  <button
                    key={v} type="button"
                    onClick={() => setButtonForm(f => ({ ...f, variant: v }))}
                    className={`btn-${v} justify-center text-xs py-1.5 ${buttonForm.variant === v ? 'ring-2 ring-offset-1 ring-accent' : ''}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Icon (optional)">
              <IconPicker value={buttonForm.icon} onChange={icon => setButtonForm(f => ({ ...f, icon }))} />
            </Field>

            <Field label="Preparation" description="Runs this Preparation when clicked. Set either this or Destination plate, not both.">
              <select value={buttonForm.preparationId} onChange={e => setButtonForm(f => ({ ...f, preparationId: e.target.value }))} className="field">
                <option value="">— none —</option>
                {(preparations as BPreparation[]).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>

            <Field label="Destination plate" description="Navigates to this Plate when clicked, instead of running a Preparation.">
              <select value={buttonForm.destinationPlateId} onChange={e => setButtonForm(f => ({ ...f, destinationPlateId: e.target.value }))} className="field">
                <option value="">— none —</option>
                {(plates as BPlate[]).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>

            <div className="pt-1 border-t border-slate-100">
              <p className="text-xs text-slate-400 mb-2">Preview</p>
              <ButtonPreview label={buttonForm.label || 'Button'} variant={buttonForm.variant} icon={buttonForm.icon} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => { setAddButtonSection(null); setEditButton(null); setButtonForm(BLANK_BTN) }}>Cancel</Button>
            <Button
              onClick={() => editButton ? saveBtn.mutate() : addBtn.mutate()}
              disabled={!buttonForm.label || addBtn.isPending || saveBtn.isPending}
            >
              {addBtn.isPending || saveBtn.isPending ? 'Saving…' : editButton ? 'Save' : 'Add'}
            </Button>
          </div>
        </Modal>
      )}

      {/* ── Section modals ─────────────────────────────────────── */}
      {(showAddSection || editSection) && (
        <Modal title={editSection ? 'Edit section' : 'Add section'} onClose={() => { setShowAddSection(false); setEditSection(null) }} size="sm">
          <SectionForm form={sectionForm} onChange={setSectionForm} totalUsed={totalWidth - (editSection?.width ?? 0)} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => { setShowAddSection(false); setEditSection(null) }}>Cancel</Button>
            <Button
              onClick={() => (editSection ? saveSection : addSection).mutate()}
              disabled={addSection.isPending || saveSection.isPending}
            >
              {addSection.isPending || saveSection.isPending ? 'Saving…' : editSection ? 'Save' : 'Add'}
            </Button>
          </div>
        </Modal>
      )}
      {deleteSection && (
        <ConfirmDialog
          title="Remove section"
          message={`Remove "${deleteSection.title || `Section ${deleteSection.rank + 1}`}" and all its views?`}
          onCancel={() => setDeleteSection(null)}
          onConfirm={() => delSection.mutate(deleteSection.id)}
          loading={delSection.isPending}
        />
      )}
      {deleteRow && (
        <ConfirmDialog
          title="Remove row"
          message={deleteRow.viewCount > 0 ? `Remove this row and its ${deleteRow.viewCount} view(s)?` : 'Remove this empty row?'}
          onCancel={() => setDeleteRow(null)}
          onConfirm={() => delRow.mutate(deleteRow.id)}
          loading={delRow.isPending}
        />
      )}

      {/* ── View wizard ────────────────────────────────────────── */}
      {(showAddView || editView) && (
        <ViewWizard
          title={editView ? 'Edit view' : 'Add view'}
          initialValues={wizardInitial}
          drinkers={drinkers as BDrinker[]}
          inputParams={plate.inputParams}
          plates={plates as BPlate[]}
          preparations={preparations as BPreparation[]}
          onClose={() => { setShowAddView(null); setEditView(null) }}
          onSave={(data) => {
            if (editView) saveViewMutation.mutate(data)
            else addViewMutation.mutate({ sectionId: showAddView!.sectionId, rowId: showAddView!.rowId, data })
          }}
          isSaving={addViewMutation.isPending || saveViewMutation.isPending}
        />
      )}
      {deleteView && (
        <ConfirmDialog
          title="Remove view"
          message={`Remove view "${deleteView.title}"?`}
          onCancel={() => setDeleteView(null)}
          onConfirm={() => delView.mutate(deleteView.id)}
          loading={delView.isPending}
        />
      )}

      {/* ── Param modals ───────────────────────────────────────── */}
      {(showAddParam || editParam) && (
        <Modal title={editParam ? 'Edit parameter' : 'Add parameter'} onClose={() => { setShowAddParam(false); setEditParam(null) }} size="sm">
          <ParamForm form={paramForm} onChange={setParamForm} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => { setShowAddParam(false); setEditParam(null) }}>Cancel</Button>
            <Button
              onClick={() => (editParam ? saveParam : addParam).mutate()}
              disabled={!paramForm.name || addParam.isPending || saveParam.isPending}
            >
              {addParam.isPending || saveParam.isPending ? 'Saving…' : editParam ? 'Save' : 'Add'}
            </Button>
          </div>
        </Modal>
      )}
      {deleteParam && (
        <ConfirmDialog
          title="Remove parameter"
          message={`Remove parameter "${deleteParam.name}"?`}
          onCancel={() => setDeleteParam(null)}
          onConfirm={() => delParam.mutate(deleteParam.id)}
          loading={delParam.isPending}
        />
      )}
      {showDeletePlate && plate && (
        <ConfirmDialog
          title="Delete plate"
          message={`Delete plate "${plate.name}"? This will remove all sections, views, and run history.`}
          onCancel={() => setShowDeletePlate(false)}
          onConfirm={() => deletePlateMutation.mutate()}
          loading={deletePlateMutation.isPending}
        />
      )}

      {/* ── Plate modal ─────────────────────────────────────────── */}
      {showEditPlate && (
        <Modal title="Edit plate" onClose={() => setShowEditPlate(false)} size="sm">
          <div className="space-y-3">
            <Field label="Name *">
              <input value={plateForm.name} onChange={e => setPlateForm(f => ({ ...f, name: e.target.value }))} className="field" autoFocus />
            </Field>
            <Field label="Description">
              <input value={plateForm.description} onChange={e => setPlateForm(f => ({ ...f, description: e.target.value }))} className="field" />
            </Field>
            <Field label="Subject type" description="Links this plate to a specific entity type. Used to pass a subject ID at run time.">
              <input value={plateForm.subjectType} onChange={e => setPlateForm(f => ({ ...f, subjectType: e.target.value }))} placeholder="e.g. Company, Order (optional)" className="field" />
            </Field>
            <Field label="Container width">
              <Select
                value={plateForm.width || '_default'}
                onValueChange={(v) => setPlateForm(f => ({ ...f, width: v === '_default' ? '' : v }))}
              >
                {WIDTH_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </Select>
            </Field>
            <Field label="Header title" description="Shown in the workspace navbar instead of that shell's default label.">
              <input value={plateForm.headerTitle} onChange={e => setPlateForm(f => ({ ...f, headerTitle: e.target.value }))} className="field" />
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowEditPlate(false)}>Cancel</Button>
            <Button onClick={() => savePlate.mutate()} disabled={!plateForm.name || savePlate.isPending}>
              {savePlate.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ── ViewCard ─────────────────────────────────────────────────────────────────

function ViewCard({ view, drinkers, inputParams, onEdit, onDelete }: {
  view: BPlateView; drinkers: BDrinker[]; inputParams: BPlateInputParam[]; onEdit: () => void; onDelete: () => void
}) {
  const drinker    = drinkers.find(d => d.id === view.drinkerId)
  const inputParam = inputParams.find(p => p.id === view.inputParamId)
  const CATEGORY_COLOR: Record<string, string> = {
    table: 'bg-blue-50 text-blue-600', list: 'bg-slate-100 text-slate-500',
    chart: 'bg-violet-50 text-violet-600', card: 'bg-amber-50 text-amber-600',
    button: 'bg-emerald-50 text-emerald-600', preparation: 'bg-teal-50 text-teal-600',
  }
  const colCount = view.columnConfig?.length ?? 0
  return (
    <div className="border border-slate-200 rounded p-2.5 bg-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GripVertical size={11} className="text-slate-300" />
          <span className="text-sm font-medium text-slate-700">{view.title}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${CATEGORY_COLOR[view.category] ?? 'bg-slate-100 text-slate-500'}`}>
            {view.category}
          </span>
        </div>
        <div className="flex gap-1.5">
          <button onClick={onEdit}   className="text-slate-400 hover:text-slate-600 transition-colors"><Pencil size={11} /></button>
          <button onClick={onDelete} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={11} /></button>
        </div>
      </div>
      <div className="mt-1 flex items-center gap-3 text-theme-xs text-slate-400 flex-wrap">
        {view.category === 'preparation'
          ? <span className="text-teal-600">Journey wizard</span>
          : inputParam
            ? <span>Coffee API · <span className="font-mono text-slate-600">{inputParam.name}</span></span>
            : drinker
              ? <span>Drinker: <span className="font-mono text-slate-600">{drinker.title}</span></span>
              : <span className="text-amber-500">No data source</span>
        }
        {colCount > 0 && <span>{colCount} col{colCount !== 1 ? 's' : ''}</span>}
        <span>rank {view.rank} · max {view.maxHeight}px</span>
      </div>
    </div>
  )
}

// ── SectionForm ──────────────────────────────────────────────────────────────

function SectionForm({
  form, onChange, totalUsed,
}: {
  form: { title: string; rank: number; width: number }
  onChange: (f: { title: string; rank: number; width: number }) => void
  totalUsed: number
}) {
  const remaining = 100 - totalUsed
  return (
    <div className="space-y-4">
      <Field label="Title (optional)">
        <input value={form.title} onChange={e => onChange({ ...form, title: e.target.value })} placeholder="e.g. Overview" className="field" />
      </Field>
      <Field label="Rank">
        <input type="number" min={0} value={form.rank} onChange={e => onChange({ ...form, rank: Number(e.target.value) })} className="field" />
        <p className="text-xs text-neutral-400 mt-1">Sections are displayed left-to-right in ascending rank order.</p>
      </Field>
      <Field label="Width % *">
        <input type="number" min={1} max={100} value={form.width}
          onChange={e => onChange({ ...form, width: Number(e.target.value) })} className="field" />
        <p className={`text-xs mt-1 ${form.width > remaining ? 'text-red-500' : 'text-neutral-400'}`}>
          {remaining}% available from remaining width budget.
        </p>
      </Field>
    </div>
  )
}

// ── ViewWizard ────────────────────────────────────────────────────────────────

interface ColState {
  key: string
  enabled: boolean
  label: string
  isLink: boolean
  linkPlateId: string
  linkInputParamName: string
  linkValueTemplate: string
  // Buckets this column into one of a 'card' view's untitled blocks (see CardView's
  // grouped-document mode) — columns sharing the same group render together, in the order
  // groups first appear here. Leave empty for a plain ungrouped field.
  group: string
  // Card views only — whether this line shows its label at all, and if so, whether the label
  // sits on its own line above the value (default) or inline on the same line as the value.
  showLabel: boolean
  labelInline: boolean
  // When the resolved value is empty: false (default) renders "—" as before; true renders nothing.
  hideWhenEmpty: boolean
  // An Ingredient name (e.g. "Decimal, 2 points", "_document") that decides how this column
  // renders — auto-derived from the matching trait's ingredient when this column is discovered
  // (see mergeColKeys); undefined means "no matching trait" (a computed field, typically).
  format?: string
  // Card views (nav tiles): a Lucide icon name from BUTTON_ICONS shown above the value.
  icon: string
  // Every other saved setting of this column the editor has no control for (description,
  // badgeMap, isDate, fallbackKey, ...) — carried through untouched on save. Without this, opening
  // and saving a view silently dropped them (e.g. every Home tile lost its icon).
  extra: Partial<BPlateViewColumnDef>
  // Wizard-only — not persisted as its own field. Columns are sorted by this ascending before
  // being written to columnConfig, whose array order *is* every render path's display order.
  rank: number
}

function ViewWizard({
  title, initialValues, drinkers, inputParams, plates, preparations, onClose, onSave, isSaving,
}: {
  title: string
  initialValues: ViewFormValues
  drinkers: BDrinker[]
  inputParams: BPlateInputParam[]
  plates: BPlate[]
  preparations: BPreparation[]
  onClose: () => void
  onSave: (data: ViewFormValues) => void
  isSaving: boolean
}) {
  const [vals, setVals] = useState<ViewFormValues>(initialValues)
  const [step, setStep] = useState(1)

  // A column's key is a trait's own title (the "<Cup Title>.<field>" bean key) for any plain,
  // non-computed field — so a newly-discovered column can look up its real ingredient here and
  // auto-derive the right format, instead of the admin having to know/re-specify a type that's
  // already defined once on the trait itself. Rendering (decimals, "_document" preview, ...) is
  // interpreted from the ingredient name at render time — see PlateViewRenderer.tsx.
  const { data: traits = [] } = useQuery({
    queryKey: ['traits'],
    queryFn: () => getTraits().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const formatByTraitTitle = new Map<string, string>()
  for (const t of traits) {
    if (t.ingredient?.name) formatByTraitTitle.set(t.title, t.ingredient.name)
  }

  // Every ingredient the business has, for the "Format" dropdown — the same catalog a trait's own
  // type picker uses, so nothing here is a Plate-specific subset invented separately from it.
  const { data: ingredients = [] } = useQuery({
    queryKey: ['ingredients'],
    queryFn: () => getIngredients().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  // Column editor state
  const [cols, setCols] = useState<ColState[]>(() =>
    initialValues.columnConfig.map((c, i) => ({
      key: c.key, enabled: true, label: c.label,
      isLink: !!c.isLink,
      linkPlateId: c.linkPlateId ?? '',
      linkInputParamName: c.linkInputParamName ?? '',
      linkValueTemplate: c.linkValueTemplate ?? '$$id$$',
      group: c.group ?? '',
      showLabel: c.showLabel ?? true,
      labelInline: c.labelInline ?? false,
      hideWhenEmpty: c.hideWhenEmpty ?? false,
      format: c.format,
      icon: c.icon ?? '',
      extra: c,
      rank: i,
    }))
  )

  // Row actions state
  const [rowActions, setRowActions] = useState<BPlateViewRowAction[]>(initialValues.rowActions)
  const [discovering, setDiscovering]   = useState(false)
  const [discoverError, setDiscoverError] = useState('')
  // Metadata fields available for output mappings (non-array top-level keys from drinker result)
  const [outputMappingFields, setOutputMappingFields] = useState<string[]>([])
  // Sample input values used when discovering from a drinker
  const [drinkerSampleInputs, setDrinkerSampleInputs] = useState<Record<string, string>>(() =>
    Object.fromEntries(inputParams.map(p => [p.name, p.fixedValue ?? '']))
  )

  const source = vals.inputParamId ? 'coffee' : 'drinker'
  const selectedParam = inputParams.find(p => p.id === vals.inputParamId)

  const [sampleValue, setSampleValue] = useState(() => selectedParam?.fixedValue ?? '')
  useEffect(() => {
    if (selectedParam?.fixedValue) setSampleValue(selectedParam.fixedValue)
  }, [vals.inputParamId]) // eslint-disable-line react-hooks/exhaustive-deps

  const totalSteps = 4

  const setSource = (s: 'drinker' | 'coffee') => {
    if (s === 'coffee') setVals(v => ({ ...v, drinkerId: '', resultKey: '', inputParamId: inputParams[0]?.id ?? '' }))
    else setVals(v => ({ ...v, inputParamId: '' }))
  }

  // ── Discovery helpers ──────────────────────────────────────────────────────
  // Recursively expand plain-object values into dot-notation leaf paths
  const extractDotPaths = (obj: Record<string, unknown>, prefix = ''): string[] => {
    const paths: string[] = []
    for (const [k, v] of Object.entries(obj)) {
      const key = prefix ? `${prefix}.${k}` : k
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        paths.push(...extractDotPaths(v as Record<string, unknown>, key))
      } else if (!Array.isArray(v)) {
        paths.push(key)
      }
    }
    return paths
  }

  // ── Coffee discovery ───────────────────────────────────────────────────────
  const discoverCoffee = async () => {
    if (!sampleValue.trim()) return
    setDiscovering(true)
    setDiscoverError('')
    try {
      let keys: string[] = []
      if (vals.coffeeApiMode === 'cup_list') {
        const r = await getCoffeesResolved(sampleValue.trim(), 1, 5)
        const d = r.data as any
        const detectedPath = Object.keys(d).find(k => Array.isArray(d[k]))
        const path = detectedPath ?? vals.dataPath
        if (detectedPath && detectedPath !== vals.dataPath) setVals(v => ({ ...v, dataPath: detectedPath }))
        const rowsData = d[path]
        if (Array.isArray(rowsData) && rowsData.length > 0) keys = extractDotPaths(rowsData[0] as Record<string, unknown>)
        else setDiscoverError('No rows found. Check the sample value.')
      } else if (vals.coffeeApiMode === 'children') {
        const r = await getChildCoffees(sampleValue.trim(), vals.childCupTitle.trim())
        const d = r.data as any
        const detectedPath = Object.keys(d).find(k => Array.isArray(d[k]))
        const path = detectedPath ?? vals.dataPath
        if (detectedPath && detectedPath !== vals.dataPath) setVals(v => ({ ...v, dataPath: detectedPath }))
        const rowsData = d[path]
        if (Array.isArray(rowsData) && rowsData.length > 0) keys = extractDotPaths(rowsData[0] as Record<string, unknown>)
        else setDiscoverError('No rows found. Check the sample value and child cup title.')
      } else {
        const r = await getCoffeeResolved(sampleValue.trim())
        keys = extractDotPaths(r.data as Record<string, unknown>)
      }
      if (keys.length > 0) mergeColKeys(keys)
    } catch {
      setDiscoverError('Failed to fetch sample data. Check the value and try again.')
    } finally {
      setDiscovering(false)
    }
  }

  // ── Drinker discovery ──────────────────────────────────────────────────────
  const discoverDrinker = async () => {
    if (!vals.drinkerId) { setDiscoverError('Select a drinker first.'); return }
    setDiscovering(true)
    setDiscoverError('')
    try {
      const r = await runDrinker(vals.drinkerId, drinkerSampleInputs)
      const payload = r.data
      if (payload.error) { setDiscoverError(payload.error); return }

      const resultKey = vals.resultKey
      const raw = resultKey ? (payload.data[resultKey] as Record<string, unknown> | null) : null
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        setDiscoverError(resultKey ? `Result key "${resultKey}" not found or not an object.` : 'Set the result key first.')
        return
      }

      // Auto-detect dataPath: first key whose value is an array
      const detectedPath = Object.keys(raw).find(k => Array.isArray(raw[k]))
      const path = detectedPath ?? vals.dataPath
      if (detectedPath && detectedPath !== vals.dataPath) setVals(v => ({ ...v, dataPath: detectedPath }))

      const arr = raw[path]
      if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null) {
        mergeColKeys(extractDotPaths(arr[0] as Record<string, unknown>))
      }

      // Collect scalar top-level fields for output mappings
      setOutputMappingFields(Object.entries(raw).filter(([, v]) => !Array.isArray(v)).map(([k]) => k))
    } catch {
      setDiscoverError('Failed to run drinker. Check the configuration.')
    } finally {
      setDiscovering(false)
    }
  }

  const mergeColKeys = (keys: string[]) => {
    setCols(prev => {
      const existing = new Set(prev.map(c => c.key))
      const merged = [...prev]
      for (const k of keys) {
        if (!existing.has(k)) {
          merged.push({
            key: k, enabled: false, label: k, isLink: false, linkPlateId: '', linkInputParamName: '', linkValueTemplate: '$$id$$',
            group: '', showLabel: true, labelInline: false, hideWhenEmpty: false,
            format: formatByTraitTitle.get(k),
            icon: '', extra: {},
            rank: merged.length,
          })
        }
      }
      return merged
    })
  }

  const handleSave = () => {
    const columnConfig: BPlateViewColumnDef[] = [...cols]
      .sort((a, b) => a.rank - b.rank)
      .filter(c => c.enabled)
      .map(c => {
        // Start from what was saved, minus the link fields (re-added below only while linked).
        const { isLink: _l, linkPlateId: _p, linkInputParamName: _n, linkValueTemplate: _t, ...extra } = c.extra
        return {
          ...extra,
          key: c.key,
          label: c.label || c.key,
          group: c.group || undefined,
          showLabel: c.showLabel ? undefined : false,
          labelInline: c.labelInline ? true : undefined,
          hideWhenEmpty: c.hideWhenEmpty ? true : undefined,
          format: c.format || undefined,
          icon: c.icon || undefined,
          ...(c.isLink && {
            isLink: true as const,
            linkPlateId: c.linkPlateId || undefined,
            linkInputParamName: c.linkInputParamName || undefined,
            linkValueTemplate: c.linkValueTemplate || undefined,
          }),
        }
      })
    onSave({ ...vals, columnConfig, rowActions })
  }

  const stepLabels = ['Settings', 'Data source', 'Columns', 'Styling']
  const canNext = step === 1 ? !!vals.title.trim() : true

  // Merges a single spacing field into vals.spacing, seeding the rest at 0 the first time any
  // field is touched — vals.spacing staying undefined (never touched) is what lets an existing
  // view keep its current spacing when this optional step is skipped entirely.
  const setSpacing = (field: keyof BPlateViewSpacing, value: number) => setVals(v => ({
    ...v,
    spacing: {
      paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0,
      marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: 0,
      ...v.spacing,
      [field]: value,
    },
  }))

  return (
    <Modal title={title} onClose={onClose} size="lg">
      {/* Step indicator */}
      <div className="flex items-center mb-5">
        {stepLabels.map((label, i) => (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-1.5 shrink-0">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                step === i + 1 ? 'bg-blue-600 text-white'
                : step > i + 1 ? 'bg-blue-100 text-blue-600'
                : 'bg-slate-100 text-slate-400'
              }`}>{i + 1}</span>
              <span className={`text-xs ${step === i + 1 ? 'text-blue-600 font-medium' : step > i + 1 ? 'text-blue-400' : 'text-slate-400'}`}>
                {label}
              </span>
            </div>
            {i < stepLabels.length - 1 && (
              <div className={`flex-1 h-px mx-2 ${step > i + 1 ? 'bg-blue-200' : 'bg-slate-100'}`} />
            )}
          </div>
        ))}
      </div>

      {/* ── Step 1: Settings ───────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-3">
          <Field label="Title *">
            <input value={vals.title} onChange={e => setVals(v => ({ ...v, title: e.target.value }))} className="field" autoFocus />
          </Field>
          <Field label="Description">
            <input value={vals.description} onChange={e => setVals(v => ({ ...v, description: e.target.value }))} className="field" />
          </Field>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
              <input type="checkbox" checked={vals.showTitle} onChange={e => setVals(v => ({ ...v, showTitle: e.target.checked }))} />
              Show title
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
              <input type="checkbox" checked={vals.showDescription} onChange={e => setVals(v => ({ ...v, showDescription: e.target.checked }))} />
              Show description
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select value={vals.category} onChange={e => setVals(v => ({ ...v, category: e.target.value as BPlateViewCategory }))} className="field">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Rank">
              <input type="number" min={0} value={vals.rank} onChange={e => setVals(v => ({ ...v, rank: Number(e.target.value) }))} className="field" />
            </Field>
          </div>
          {vals.category === 'chart' && (
            <Field label="Default chart type">
              <select value={vals.chartType} onChange={e => setVals(v => ({ ...v, chartType: e.target.value as BPlateChartType }))} className="field">
                {CHART_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          )}
          {vals.category === 'preparation' && (
            <Field label="Preparation *" description="The preparation whose journey wizard will be embedded in this view">
              <select value={vals.preparationId} onChange={e => setVals(v => ({ ...v, preparationId: e.target.value }))} className="field">
                <option value="">— select preparation —</option>
                {preparations.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          )}
          {vals.category === 'preparation' && (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input type="checkbox" checked={vals.inline} onChange={e => setVals(v => ({ ...v, inline: e.target.checked }))} className="rounded w-4 h-4" />
              Show the form on the page (started with the Plate's inputs) instead of a button that opens it
            </label>
          )}
          <Field label="Max height (px)">
            <input type="number" min={0} value={vals.maxHeight} onChange={e => setVals(v => ({ ...v, maxHeight: Number(e.target.value) }))} className="field" />
            <p className="text-xs text-neutral-400 mt-1">0 = unlimited.</p>
          </Field>

          {/* Pagination */}
          <div className="border border-slate-100 rounded-lg p-3 space-y-3">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={!!vals.paginationConfig}
                onChange={e => setVals(v => ({
                  ...v,
                  paginationConfig: e.target.checked
                    ? { pageParamName: '', totalPagesPath: 'totalPages', currentPagePath: 'page' }
                    : undefined,
                }))}
              />
              Paginated view
            </label>
            {vals.paginationConfig && (
              <div className="space-y-2 pl-4 border-l-2 border-slate-100">
                <div>
                  <label className="block text-theme-xs text-slate-500 mb-0.5">Page param <span className="text-red-400">*</span></label>
                  <select
                    value={vals.paginationConfig.pageParamName}
                    onChange={e => setVals(v => ({ ...v, paginationConfig: { ...v.paginationConfig!, pageParamName: e.target.value } }))}
                    className="field text-xs"
                  >
                    <option value="">— select param —</option>
                    {inputParams.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                  </select>
                  <p className="text-theme-xs text-slate-400 mt-0.5">Input param that holds the current page number.</p>
                </div>
                <div>
                  <label className="block text-theme-xs text-slate-500 mb-0.5">Total pages path</label>
                  <input
                    value={vals.paginationConfig.totalPagesPath}
                    onChange={e => setVals(v => ({ ...v, paginationConfig: { ...v.paginationConfig!, totalPagesPath: e.target.value } }))}
                    className="field text-xs font-mono"
                    placeholder="e.g. totalPages or result.totalPages"
                  />
                </div>
                <div>
                  <label className="block text-theme-xs text-slate-500 mb-0.5">Current page path</label>
                  <input
                    value={vals.paginationConfig.currentPagePath}
                    onChange={e => setVals(v => ({ ...v, paginationConfig: { ...v.paginationConfig!, currentPagePath: e.target.value } }))}
                    className="field text-xs font-mono"
                    placeholder="e.g. page or result.page"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: Data source ─────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          <Field label="Data source">
            <select value={source} onChange={e => setSource(e.target.value as 'drinker' | 'coffee')} className="field">
              <option value="drinker">Drinker script</option>
              <option value="coffee">Coffee API</option>
            </select>
          </Field>

          {source === 'coffee' ? (
            <div className="space-y-3">
              <Field label="Input parameter">
                <select value={vals.inputParamId} onChange={e => setVals(v => ({ ...v, inputParamId: e.target.value }))} className="field">
                  <option value="">— select —</option>
                  {inputParams.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.paramType})</option>
                  ))}
                </select>
              </Field>
              <Field label="Child cup title (optional)">
                <input
                  value={vals.childCupTitle}
                  onChange={e => setVals(v => ({ ...v, childCupTitle: e.target.value }))}
                  placeholder="e.g. order"
                  className="field font-mono"
                />
                <p className="text-xs text-neutral-400 mt-1">
                  Used when fetching child coffees — set to the cup title of the child records.
                </p>
              </Field>
              <Field label="Data path">
                <input
                  value={vals.dataPath}
                  onChange={e => setVals(v => ({ ...v, dataPath: e.target.value }))}
                  placeholder="rows"
                  className="field font-mono"
                />
                <p className="text-xs text-neutral-400 mt-1">
                  Key inside the response that holds the data array (supports dot-notation, e.g. <span className="font-mono">result.rows</span>).
                </p>
              </Field>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Drinker">
                <select value={vals.drinkerId} onChange={e => setVals(v => ({ ...v, drinkerId: e.target.value }))} className="field">
                  <option value="">— none —</option>
                  {drinkers.map(d => <option key={d.id} value={d.id}>{d.title} ({d.resultKey})</option>)}
                </select>
              </Field>
              <Field label="Result key">
                <input value={vals.resultKey} onChange={e => setVals(v => ({ ...v, resultKey: e.target.value }))} placeholder="e.g. orders" className="field font-mono" />
                <p className="text-xs text-neutral-400 mt-1">Key extracted from the drinker output object (matches the drinker's result key).</p>
              </Field>
              <Field label="Data path">
                <input
                  value={vals.dataPath}
                  onChange={e => setVals(v => ({ ...v, dataPath: e.target.value }))}
                  placeholder="e.g. data"
                  className="field font-mono"
                />
                <p className="text-xs text-neutral-400 mt-1">
                  Dot-notation path inside the result to the data array (e.g. <span className="font-mono">data</span> or <span className="font-mono">result.items</span>). Auto-detected on discovery.
                </p>
              </Field>
            </div>
          )}
        </div>
      )}

      {/* ── Step 3: Columns ─────────────────────────────────────── */}
      {step === 3 && source === 'coffee' && (
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-slate-600">Discover columns from sample data</p>
            <select
              value={vals.coffeeApiMode}
              onChange={e => setVals(v => ({ ...v, coffeeApiMode: e.target.value as ViewFormValues['coffeeApiMode'] }))}
              className="field text-xs"
            >
              <option value="cup_list">Coffee list — by cup ID</option>
              <option value="single">Single coffee — by coffee ID</option>
              <option value="children">Child coffees — by parent coffee ID</option>
            </select>
            <div className="flex gap-2">
              <input
                value={sampleValue}
                onChange={e => setSampleValue(e.target.value)}
                placeholder={
                  vals.coffeeApiMode === 'cup_list' ? 'Enter a cup ID'
                  : vals.coffeeApiMode === 'children' ? 'Enter a parent coffee ID'
                  : 'Enter a coffee ID'
                }
                className="field flex-1 font-mono text-xs"
              />
              <Button
                variant="secondary" size="sm"
                onClick={discoverCoffee}
                disabled={discovering || !sampleValue.trim()}
                className="shrink-0"
                loading={discovering}
              >
                {discovering ? 'Loading…' : 'Discover'}
              </Button>
            </div>
            {discoverError && <p className="text-xs text-red-500">{discoverError}</p>}
          </div>
          <ColumnList cols={cols} setCols={setCols} plates={plates} ingredients={ingredients} />
          <p className="text-xs text-slate-400">
            {cols.filter(c => c.enabled).length} of {cols.length} column{cols.length !== 1 ? 's' : ''} selected.
            Leave all unchecked to show all fields.
          </p>
          {vals.category === 'table' && (
            <TotalsEditor rows={vals.summaryRows} cols={cols} onChange={summaryRows => setVals(v => ({ ...v, summaryRows }))} />
          )}

          {vals.coffeeApiMode === 'cup_list' && (
            <>
              <FilterConditionsEditor
                conditions={vals.filterConditions}
                onChange={fc => setVals(v => ({ ...v, filterConditions: fc }))}
                inputParams={inputParams}
              />
              <OrderByEditor
                orderBy={vals.orderBy}
                onChange={ob => setVals(v => ({ ...v, orderBy: ob }))}
              />
            </>
          )}
        </div>
      )}

      {step === 3 && source === 'drinker' && (
        <div className="space-y-4">
          {/* Drinker discovery panel */}
          <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-slate-600">Run drinker to discover columns</p>
            {inputParams.length > 0 && (
              <div className="space-y-1.5">
                {inputParams.map(p => (
                  <div key={p.id} className="flex items-center gap-2">
                    <span className="text-theme-xs font-mono text-slate-500 w-28 shrink-0">{p.name}</span>
                    <input
                      value={drinkerSampleInputs[p.name] ?? ''}
                      onChange={e => setDrinkerSampleInputs(prev => ({ ...prev, [p.name]: e.target.value }))}
                      placeholder={p.fixedValue ? `fixed: ${p.fixedValue}` : `sample ${p.name}`}
                      className="field flex-1 text-xs py-0.5 font-mono"
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center">
              <p className="text-theme-xs text-slate-400 font-mono">
                {vals.dataPath ? `path: ${vals.dataPath}` : 'path: auto-detected'}
              </p>
              <Button
                variant="secondary" size="sm"
                onClick={discoverDrinker}
                disabled={discovering || !vals.drinkerId || !vals.resultKey}
                loading={discovering}
              >
                {discovering ? 'Running…' : 'Run & Discover'}
              </Button>
            </div>
            {discoverError && <p className="text-xs text-red-500">{discoverError}</p>}
          </div>

          {/* Column selection */}
          <ColumnList cols={cols} setCols={setCols} plates={plates} ingredients={ingredients} />
          <p className="text-xs text-slate-400">
            {cols.filter(c => c.enabled).length} of {cols.length} column{cols.length !== 1 ? 's' : ''} selected.
            Leave all unchecked to show all fields.
          </p>
          {vals.category === 'table' && (
            <TotalsEditor rows={vals.summaryRows} cols={cols} onChange={summaryRows => setVals(v => ({ ...v, summaryRows }))} />
          )}

          {/* Output → Input param mappings */}
          {outputMappingFields.length > 0 && (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <p className="text-xs font-medium text-slate-600">Output → Input param mappings</p>
              <p className="text-theme-xs text-slate-400">
                Map scalar result fields to input parameters (e.g. pagination: <span className="font-mono">page → PAGE</span>).
              </p>
              {outputMappingFields.map(field => {
                const existing = vals.outputMappings.find(m => m.fromPath === field)
                return (
                  <div key={field} className="flex items-center gap-2">
                    <span className="text-theme-xs font-mono text-slate-600 w-28 shrink-0">{field}</span>
                    <span className="text-[10px] text-slate-400">→</span>
                    <input
                      value={existing?.toParamName ?? ''}
                      onChange={e => {
                        const val = e.target.value.trim()
                        setVals(v => {
                          const others = v.outputMappings.filter(m => m.fromPath !== field)
                          return { ...v, outputMappings: val ? [...others, { fromPath: field, toParamName: val }] : others }
                        })
                      }}
                      className="field flex-1 text-xs py-0.5 font-mono"
                      placeholder="input param name (leave empty to skip)"
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Row actions (table category only, step 3) ────────────── */}
      {step === 3 && vals.category === 'table' && (
        <div className="border-t border-slate-100 pt-4 mt-4">
          <RowActionsEditor
            actions={rowActions}
            onChange={setRowActions}
            plates={plates}
            preparations={preparations}
          />
        </div>
      )}

      {/* ── Step 4: Styling (optional — skip by saving from step 3) ── */}
      {step === 4 && (
        <div className="space-y-4">
          <div className="border border-slate-100 rounded-lg p-3 space-y-3">
            <p className="text-xs font-medium text-slate-700">Colors &amp; border</p>
            <p className="text-theme-xs text-slate-400 leading-relaxed">
              These CSS values are applied directly to the view panel.
              Use any valid CSS color (<span className="font-mono">#fff</span>, <span className="font-mono">rgb(…)</span>, color name) for color fields.
              Use CSS lengths (<span className="font-mono">8px</span>, <span className="font-mono">0.5rem</span>) for width and radius.
              Leave a field empty to keep the default style.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-theme-xs text-slate-500 mb-0.5">Background color</label>
                <div className="flex gap-1.5 items-center">
                  <input
                    type="color"
                    value={vals.backgroundColor || '#ffffff'}
                    onChange={e => setVals(v => ({ ...v, backgroundColor: e.target.value }))}
                    className="h-7 w-8 rounded cursor-pointer border border-slate-200 p-0.5"
                  />
                  <input
                    value={vals.backgroundColor}
                    onChange={e => setVals(v => ({ ...v, backgroundColor: e.target.value }))}
                    className="field text-xs font-mono flex-1"
                    placeholder="e.g. #f9fafb"
                  />
                </div>
              </div>
              <div>
                <label className="block text-theme-xs text-slate-500 mb-0.5">Border color</label>
                <div className="flex gap-1.5 items-center">
                  <input
                    type="color"
                    value={vals.borderColor || '#e4e7ec'}
                    onChange={e => setVals(v => ({ ...v, borderColor: e.target.value }))}
                    className="h-7 w-8 rounded cursor-pointer border border-slate-200 p-0.5"
                  />
                  <input
                    value={vals.borderColor}
                    onChange={e => setVals(v => ({ ...v, borderColor: e.target.value }))}
                    className="field text-xs font-mono flex-1"
                    placeholder="e.g. #e4e7ec"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-theme-xs text-slate-500 mb-0.5">Border style</label>
                <select value={vals.borderStyle} onChange={e => setVals(v => ({ ...v, borderStyle: e.target.value }))} className="field text-xs">
                  <option value="">default</option>
                  <option value="none">none</option>
                  <option value="solid">solid</option>
                  <option value="dashed">dashed</option>
                  <option value="dotted">dotted</option>
                  <option value="double">double</option>
                </select>
              </div>
              <div>
                <label className="block text-theme-xs text-slate-500 mb-0.5">Border width</label>
                <input
                  value={vals.borderWidth}
                  onChange={e => setVals(v => ({ ...v, borderWidth: e.target.value }))}
                  className="field text-xs font-mono"
                  placeholder="e.g. 2px"
                />
              </div>
              <div>
                <label className="block text-theme-xs text-slate-500 mb-0.5">Border radius</label>
                <input
                  value={vals.borderRadius}
                  onChange={e => setVals(v => ({ ...v, borderRadius: e.target.value }))}
                  className="field text-xs font-mono"
                  placeholder="e.g. 8px"
                />
              </div>
            </div>
          </div>

          <div className="border border-slate-100 rounded-lg p-3 space-y-3">
            <p className="text-xs font-medium text-slate-700">Spacing</p>
            <p className="text-theme-xs text-slate-400 leading-relaxed">
              Numbers on Tailwind's spacing scale (0, 1, 2, 3…), applied to the view's content area.
              Leaving every field at 0 (and never touching this section) keeps the view's normal
              default spacing — this only takes effect once you set at least one value.
            </p>
            <div>
              <p className="text-theme-xs text-slate-500 mb-1">Padding (top / right / bottom / left)</p>
              <div className="grid grid-cols-4 gap-2">
                {(['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] as const).map(field => (
                  <input
                    key={field}
                    type="number" min={0}
                    value={vals.spacing?.[field] ?? 0}
                    onChange={e => setSpacing(field, Number(e.target.value))}
                    className="field text-xs"
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="text-theme-xs text-slate-500 mb-1">Margin (top / right / bottom / left)</p>
              <div className="grid grid-cols-4 gap-2">
                {(['marginTop', 'marginRight', 'marginBottom', 'marginLeft'] as const).map(field => (
                  <input
                    key={field}
                    type="number" min={0}
                    value={vals.spacing?.[field] ?? 0}
                    onChange={e => setSpacing(field, Number(e.target.value))}
                    className="field text-xs"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-paper-border mt-4">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <div className="flex gap-2">
          {step > 1 && (
            <Button variant="secondary" onClick={() => setStep(s => s - 1)}>Back</Button>
          )}
          {step === totalSteps - 1 && (
            <Button variant="secondary" onClick={handleSave} disabled={!canNext || isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          )}
          <Button
            onClick={step < totalSteps ? () => setStep(s => s + 1) : handleSave}
            disabled={!canNext || isSaving}
          >
            {isSaving ? 'Saving…' : step < totalSteps ? 'Next' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ── ColumnList ────────────────────────────────────────────────────────────────

function ColumnList({ cols, setCols, plates, ingredients }: {
  cols: ColState[]
  setCols: Dispatch<SetStateAction<ColState[]>>
  plates: BPlate[]
  ingredients: BIngredient[]
}) {
  if (cols.length === 0) {
    return <p className="text-xs text-slate-400 text-center py-4">No columns yet — run a discovery or add columns manually.</p>
  }
  return (
    <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
      {cols.map((col, i) => (
        <ColRow
          key={col.key}
          col={col}
          plates={plates}
          ingredients={ingredients}
          onChange={updated => setCols(prev => prev.map((c, j) => j === i ? updated : c))}
        />
      ))}
    </div>
  )
}

// ── ColRow ────────────────────────────────────────────────────────────────────

function ColRow({ col, plates, ingredients, onChange }: {
  col: ColState
  plates: BPlate[]
  ingredients: BIngredient[]
  onChange: (updated: ColState) => void
}) {
  const up = (partial: Partial<ColState>) => onChange({ ...col, ...partial })

  return (
    <div className={`border rounded-lg p-2.5 space-y-2 ${col.enabled ? 'border-blue-100 bg-blue-50/30' : 'border-slate-100 bg-white'}`}>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={col.enabled}
          onChange={e => up({ enabled: e.target.checked })}
          className="rounded shrink-0"
        />
        <span className="font-mono text-xs text-slate-600 shrink-0">{col.key}</span>
        {col.enabled && (
          <>
            <input
              value={col.label}
              onChange={e => up({ label: e.target.value })}
              placeholder={col.key}
              className="field flex-1 text-xs py-0.5"
            />
            <input
              type="number"
              value={col.rank}
              onChange={e => up({ rank: Number(e.target.value) })}
              title="Display order — lower numbers appear first"
              className="field w-14 shrink-0 text-xs py-0.5"
            />
            <input
              value={col.group}
              onChange={e => up({ group: e.target.value })}
              placeholder="Group (card views)"
              title="Card views only: clusters this column into an untitled block alongside every other column sharing the same group name"
              className="field w-32 shrink-0 text-xs py-0.5"
            />
            <button
              onClick={() => up({ isLink: !col.isLink })}
              className={`shrink-0 p-1 rounded transition-colors ${col.isLink ? 'text-blue-600 bg-blue-100' : 'text-slate-400 hover:text-slate-600'}`}
              title="Toggle link"
            >
              <Link2 size={12} />
            </button>
          </>
        )}
      </div>

      {col.enabled && (
        <div className="flex items-center gap-3 pl-5">
          <label className="flex items-center gap-1 text-theme-xs text-slate-500 cursor-pointer" title="Card views only">
            <input type="checkbox" checked={col.showLabel} onChange={e => up({ showLabel: e.target.checked })} className="rounded" />
            Show title
          </label>
          {col.showLabel && (
            <label className="flex items-center gap-1 text-theme-xs text-slate-500 cursor-pointer" title="Card views only">
              <input type="checkbox" checked={col.labelInline} onChange={e => up({ labelInline: e.target.checked })} className="rounded" />
              Same line as value
            </label>
          )}
          <label className="flex items-center gap-1 text-theme-xs text-slate-500 cursor-pointer">
            <input type="checkbox" checked={col.hideWhenEmpty} onChange={e => up({ hideWhenEmpty: e.target.checked })} className="rounded" />
            Hide when empty
          </label>
          <label className="flex items-center gap-1 text-theme-xs text-slate-500" title="Auto-detected from the matching trait's ingredient when discovered; override here if needed">
            Format
            <select
              value={col.format ?? ''}
              onChange={e => up({ format: e.target.value || undefined })}
              className="field text-theme-xs py-0.5 w-40"
            >
              <option value="">— plain text —</option>
              {ingredients.map(ing => <option key={ing.id} value={ing.name}>{ing.name}</option>)}
            </select>
          </label>
          <div className="flex items-center gap-1 text-theme-xs text-slate-500" title="Card views: icon shown above the value (e.g. a Home tile)">
            Icon
            <div className="w-44"><IconPicker value={col.icon} onChange={icon => up({ icon })} /></div>
          </div>
        </div>
      )}

      {col.enabled && col.isLink && (
        <div className="pl-5 space-y-1.5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5">Target plate</label>
              <select value={col.linkPlateId} onChange={e => up({ linkPlateId: e.target.value })} className="field text-xs py-0.5">
                <option value="">— select plate —</option>
                {plates.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5">Input param name (optional)</label>
              <input
                value={col.linkInputParamName}
                onChange={e => up({ linkInputParamName: e.target.value })}
                placeholder="empty = page needs no input"
                className="field text-xs py-0.5 font-mono"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-slate-500 mb-0.5">Value template (only with an input param)</label>
            <input
              value={col.linkValueTemplate}
              onChange={e => up({ linkValueTemplate: e.target.value })}
              placeholder="$$id$$"
              className="field text-xs py-0.5 font-mono"
            />
            <p className="text-[10px] text-slate-400 mt-0.5">
              Use <span className="font-mono">$$key$$</span> to inject a field value. E.g. <span className="font-mono">$$id$$</span> uses the row's <span className="font-mono">id</span> field.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ── ParamForm ────────────────────────────────────────────────────────────────

function ParamForm({
  form, onChange,
}: {
  form: { name: string; description: string; paramType: BPlateInputParamType; required: boolean; fixedValue: string }
  onChange: (f: typeof form) => void
}) {
  return (
    <div className="space-y-3">
      <Field label="Name *">
        <input value={form.name} onChange={e => onChange({ ...form, name: e.target.value })} placeholder="e.g. companyId" className="field font-mono" />
      </Field>
      <Field label="Description">
        <input value={form.description} onChange={e => onChange({ ...form, description: e.target.value })} className="field" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type">
          <select value={form.paramType} onChange={e => onChange({ ...form, paramType: e.target.value as BPlateInputParamType })} className="field">
            {PARAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
            <input type="checkbox" checked={form.required} onChange={e => onChange({ ...form, required: e.target.checked })} className="rounded" />
            Required
          </label>
        </div>
      </div>
      <Field label="Fixed value (auto-passed, hides manual input)">
        <input value={form.fixedValue} onChange={e => onChange({ ...form, fixedValue: e.target.value })} placeholder="Leave empty for user input" className="field font-mono" />
      </Field>
    </div>
  )
}

// ── FilterConditionsEditor ─────────────────────────────────────────────────────

const FILTER_OPS: { value: BPlateViewFilterOp; label: string }[] = [
  { value: 'eq',       label: '=' },
  { value: 'neq',      label: '≠' },
  { value: 'contains', label: 'contains' },
  { value: 'gt',       label: '>' },
  { value: 'gte',      label: '≥' },
  { value: 'lt',       label: '<' },
  { value: 'lte',      label: '≤' },
  { value: 'null',     label: 'is empty' },
  { value: 'not_null', label: 'is not empty' },
]

const NO_VALUE_OPS: BPlateViewFilterOp[] = ['null', 'not_null']

// ── RowActionsEditor ──────────────────────────────────────────────────────────

const BLANK_ROW_ACTION: BPlateViewRowAction = {
  label: '', idField: 'id', payloadKey: 'id', preparationId: undefined, destinationPlateId: undefined, variant: undefined,
}

function RowActionsEditor({ actions, onChange, plates, preparations }: {
  actions: BPlateViewRowAction[]
  onChange: (a: BPlateViewRowAction[]) => void
  plates: BPlate[]
  preparations: BPreparation[]
}) {
  const update = (i: number, patch: Partial<BPlateViewRowAction>) =>
    onChange(actions.map((a, j) => j === i ? { ...a, ...patch } : a))
  const remove = (i: number) => onChange(actions.filter((_, j) => j !== i))

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-700">Row actions</p>
        <button
          onClick={() => onChange([...actions, { ...BLANK_ROW_ACTION }])}
          className="text-xs text-blue-600 hover:underline"
        >
          + Add action
        </button>
      </div>
      {actions.length === 0 && (
        <p className="text-theme-xs text-slate-400">No row actions — clicking a row will do nothing.</p>
      )}
      {actions.map((action, i) => (
        <div key={i} className="border border-slate-100 rounded-lg p-3 space-y-2 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <input
              value={action.label}
              onChange={e => update(i, { label: e.target.value })}
              placeholder="Button label (e.g. Edit)"
              className="field flex-1 text-xs py-1"
            />
            <select
              value={action.variant ?? ''}
              onChange={e => update(i, { variant: e.target.value === 'danger' ? 'danger' : undefined })}
              className="field text-xs py-1 w-24 shrink-0"
            >
              <option value="">Default</option>
              <option value="danger">Danger</option>
            </select>
            <button onClick={() => remove(i)} className="text-slate-300 hover:text-red-500 transition-colors shrink-0">
              <Trash2 size={12} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-slate-500 mb-0.5">ID field <span className="text-slate-400">(from row data)</span></label>
              <input
                value={action.idField}
                onChange={e => update(i, { idField: e.target.value })}
                placeholder="id"
                className="field text-xs py-0.5 font-mono w-full"
              />
            </div>
            <div>
              <label className="block text-[10px] text-slate-500 mb-0.5">Payload key <span className="text-slate-400">(journey input)</span></label>
              <input
                value={action.payloadKey}
                onChange={e => update(i, { payloadKey: e.target.value })}
                placeholder="e.g. preorder_elementId"
                className="field text-xs py-0.5 font-mono w-full"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-slate-500 mb-0.5">Preparation (journey)</label>
              <select
                value={action.preparationId ?? ''}
                onChange={e => update(i, { preparationId: e.target.value || undefined })}
                className="field text-xs py-0.5 w-full"
              >
                <option value="">— none —</option>
                {preparations.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-slate-500 mb-0.5">Destination plate</label>
              <select
                value={action.destinationPlateId ?? ''}
                onChange={e => update(i, { destinationPlateId: e.target.value || undefined })}
                className="field text-xs py-0.5 w-full"
              >
                <option value="">— none —</option>
                {plates.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

const PARENT_FILTER_OPS_ID:         BPlateViewFilterOp[] = ['eq', 'neq', 'null', 'not_null']
const PARENT_FILTER_OPS_IDENTIFIER: BPlateViewFilterOp[] = ['eq', 'neq', 'contains', 'null', 'not_null']

function FilterConditionsEditor({
  conditions,
  onChange,
  inputParams,
}: {
  conditions: BPlateViewFilterCondition[]
  onChange: (c: BPlateViewFilterCondition[]) => void
  inputParams: BPlateInputParam[]
}) {
  const isParent = (field: string) => field.includes('.')

  const addTrait  = () => onChange([...conditions, { field: '', op: 'eq', value: '' }])
  const addParent = () => onChange([...conditions, { field: '.id', op: 'eq', paramRef: '' }])
  const remove = (i: number) => onChange(conditions.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<BPlateViewFilterCondition>) =>
    onChange(conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c))

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-600">Filter conditions</p>
        <div className="flex gap-3">
          <button onClick={addParent} className="text-xs text-violet-600 hover:text-violet-700">+ Parent</button>
          <button onClick={addTrait}  className="text-xs text-blue-600 hover:text-blue-700">+ Trait</button>
        </div>
      </div>
      {conditions.length === 0 && (
        <p className="text-xs text-slate-400 italic">No filters — all records returned.</p>
      )}
      {conditions.map((cond, i) => {
        if (isParent(cond.field)) {
          const dotIdx    = cond.field.lastIndexOf('.')
          const relLabel  = cond.field.slice(0, dotIdx)
          const subField  = cond.field.slice(dotIdx + 1) as 'id' | 'identifier'
          const allowedOps = subField === 'identifier' ? PARENT_FILTER_OPS_IDENTIFIER : PARENT_FILTER_OPS_ID

          return (
            <div key={i} className="border border-violet-100 bg-violet-50/40 rounded-lg p-2.5 space-y-2">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-semibold text-violet-500 uppercase tracking-wide shrink-0">Parent</span>
                <input
                  value={relLabel}
                  onChange={e => update(i, { field: `${e.target.value}.${subField}` })}
                  placeholder="relation label"
                  className="field font-mono text-xs flex-1"
                />
                <select
                  value={subField}
                  onChange={e => {
                    const next = e.target.value as 'id' | 'identifier'
                    const validOps = next === 'identifier' ? PARENT_FILTER_OPS_IDENTIFIER : PARENT_FILTER_OPS_ID
                    const op = validOps.includes(cond.op) ? cond.op : 'eq'
                    update(i, { field: `${relLabel}.${next}`, op })
                  }}
                  className="field text-xs w-32"
                >
                  <option value="id">id (UUID)</option>
                  <option value="identifier">identifier</option>
                </select>
                <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-500 shrink-0">
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="flex gap-1.5 items-start pl-[52px]">
                <select
                  value={cond.op}
                  onChange={e => update(i, { op: e.target.value as BPlateViewFilterOp, value: '', paramRef: undefined })}
                  className="field text-xs w-28"
                >
                  {FILTER_OPS.filter(o => allowedOps.includes(o.value)).map(o =>
                    <option key={o.value} value={o.value}>{o.label}</option>
                  )}
                </select>
                {!NO_VALUE_OPS.includes(cond.op) && (
                  <div className="flex flex-col gap-0.5 flex-1">
                    <select
                      value={cond.paramRef ?? ''}
                      onChange={e => update(i, { paramRef: e.target.value || undefined, value: e.target.value ? undefined : (cond.value ?? '') })}
                      className="field text-xs"
                    >
                      <option value="">— fixed value —</option>
                      {inputParams.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                    </select>
                    {!cond.paramRef && (
                      <input
                        value={cond.value ?? ''}
                        onChange={e => update(i, { value: e.target.value })}
                        placeholder={subField === 'id' ? 'parent UUID' : 'identifier value'}
                        className="field font-mono text-xs"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        }

        // ── Trait condition row ──────────────────────────────────────────────
        return (
          <div key={i} className="flex gap-1.5 items-start">
            <input
              value={cond.field}
              onChange={e => update(i, { field: e.target.value })}
              placeholder="trait title"
              className="field font-mono text-xs flex-1"
            />
            <select
              value={cond.op}
              onChange={e => update(i, { op: e.target.value as BPlateViewFilterOp, value: '', paramRef: undefined })}
              className="field text-xs w-28"
            >
              {FILTER_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {!NO_VALUE_OPS.includes(cond.op) && (
              <div className="flex flex-col gap-0.5 flex-1">
                <input
                  value={cond.paramRef ?? cond.value ?? ''}
                  onChange={e => {
                    if (cond.paramRef !== undefined) update(i, { paramRef: e.target.value || undefined })
                    else update(i, { value: e.target.value })
                  }}
                  placeholder={cond.paramRef !== undefined ? 'param name' : 'value'}
                  className="field font-mono text-xs"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    id={`paramref-${i}`}
                    checked={cond.paramRef !== undefined}
                    onChange={e => update(i, e.target.checked
                      ? { paramRef: cond.value ?? '', value: undefined }
                      : { paramRef: undefined, value: '' }
                    )}
                    className="rounded"
                  />
                  {inputParams.length > 0 && (
                    <select
                      value={cond.paramRef ?? ''}
                      onChange={e => update(i, { paramRef: e.target.value || undefined, value: undefined })}
                      className="field text-theme-xs py-0 h-5"
                    >
                      <option value="">— from input param —</option>
                      {inputParams.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                    </select>
                  )}
                  {inputParams.length === 0 && (
                    <label htmlFor={`paramref-${i}`} className="text-theme-xs text-slate-400">from param</label>
                  )}
                </div>
              </div>
            )}
            <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-500 mt-1">
              <Trash2 size={13} />
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ── OrderByEditor ──────────────────────────────────────────────────────────────

function OrderByEditor({
  orderBy,
  onChange,
}: {
  orderBy: BPlateViewOrderBy[]
  onChange: (ob: BPlateViewOrderBy[]) => void
}) {
  const add = () => onChange([...orderBy, { field: 'created_at', dir: 'ASC' }])
  const remove = (i: number) => onChange(orderBy.filter((_, idx) => idx !== i))
  const update = (i: number, patch: Partial<BPlateViewOrderBy>) =>
    onChange(orderBy.map((ob, idx) => idx === i ? { ...ob, ...patch } : ob))

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-600">Order by</p>
        <button onClick={add} className="text-xs text-blue-600 hover:text-blue-700">+ Add sort</button>
      </div>
      {orderBy.length === 0 && (
        <p className="text-xs text-slate-400 italic">Default order: created_at ASC.</p>
      )}
      {orderBy.map((ob, i) => (
        <div key={i} className="flex gap-1.5 items-center">
          <input
            value={ob.field}
            onChange={e => update(i, { field: e.target.value })}
            placeholder="created_at / updated_at / trait title"
            className="field font-mono text-xs flex-1"
          />
          <select
            value={ob.dir}
            onChange={e => update(i, { dir: e.target.value as 'ASC' | 'DESC' })}
            className="field text-xs w-20"
          >
            <option value="ASC">ASC</option>
            <option value="DESC">DESC</option>
          </select>
          <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-500">
            <Trash2 size={13} />
          </button>
        </div>
      ))}
    </div>
  )
}

// Totals under a table (Columns step, tables only): the admin picks which column(s) to total and
// how; each line's label defaults to "<Operation> of <column label>" and its number format comes
// from the column's own. Any discovered column can be totalled, shown in the table or not (e.g. a
// line's excl.-tax amount while the table only shows the incl.-tax one).
const TOTAL_OPS: { value: NonNullable<BPlateViewSummaryRow['agg']>; label: string }[] = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'count', label: 'Count' },
]
function TotalsEditor({ rows, cols, onChange }: {
  rows: BPlateViewSummaryRow[]
  cols: ColState[]
  onChange: (rows: BPlateViewSummaryRow[]) => void
}) {
  const colLabel = (key: string) => cols.find(c => c.key === key)?.label || key
  const defaultLabel = (r: Pick<BPlateViewSummaryRow, 'key' | 'agg'>) =>
    r.key ? `${TOTAL_OPS.find(o => o.value === (r.agg ?? 'sum'))!.label} of ${colLabel(r.key)}` : ''
  // A column can be totalled whether or not it's shown; keep keys already saved even if this
  // edit session hasn't re-discovered them.
  const options = [...cols.map(c => c.key), ...rows.map(r => r.key).filter(k => k && !cols.some(c => c.key === k))]

  const update = (i: number, patch: Partial<BPlateViewSummaryRow>) => onChange(rows.map((r, j) => {
    if (j !== i) return r
    const next = { ...r, ...patch }
    // Follow the column/operation in the label until the admin types their own.
    if (!r.label || r.label === defaultLabel(r)) next.label = defaultLabel(next)
    if (patch.key !== undefined) next.format = cols.find(c => c.key === patch.key)?.format
    return next
  }))

  return (
    <div className="space-y-3 border-t border-slate-100 pt-3">
      <div>
        <p className="text-sm font-medium text-gray-700">Totals</p>
        <p className="text-xs text-gray-500">Summary lines under the table, right-aligned like an invoice. Pick a column and how to total it; mark the grand total to show it bold.</p>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1.2fr_0.8fr_1.2fr_auto_auto] sm:items-center">
          <select value={r.key} onChange={e => update(i, { key: e.target.value })} className="field">
            <option value="">— column —</option>
            {options.map(k => <option key={k} value={k}>{colLabel(k)}{colLabel(k) !== k ? ` (${k})` : ''}</option>)}
          </select>
          <select value={r.agg ?? 'sum'} onChange={e => update(i, { agg: e.target.value as BPlateViewSummaryRow['agg'] })} className="field">
            {TOTAL_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input value={r.label} onChange={e => onChange(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Label" className="field" />
          <label className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
            <input type="checkbox" checked={!!r.emphasis} onChange={e => update(i, { emphasis: e.target.checked })} className="rounded w-4 h-4" />
            Grand total
          </label>
          <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-gray-400 hover:text-error-500 justify-self-end" title="Remove"><X size={14} /></button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rows, { label: '', key: '', agg: 'sum' }])}
        disabled={options.length === 0}
        className="text-sm font-medium text-brand-500 hover:text-brand-600 disabled:text-gray-400"
      >
        + Add total
      </button>
      {options.length === 0 && <p className="text-xs text-gray-400">Discover columns above first.</p>}
    </div>
  )
}
